import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { toBlob } from "html-to-image";
import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbLoader2, TbPhoto } from "react-icons/tb";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

type MarkdownTableProps = {
    children: ReactNode;
};

/** Browsers accept oklch; canvas fillStyle normalizes it to rgb/hex for libraries that do not. */
function toRgbColor(color: string): string {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return "#ffffff";
    }
    ctx.fillStyle = color;
    return ctx.fillStyle;
}

export function MarkdownTable({ children }: MarkdownTableProps) {
    const { t } = useTranslation();
    const tableRef = useRef<HTMLDivElement>(null);
    const [isCopying, setIsCopying] = useState(false);

    const handleCopyAsImage = async () => {
        const element = tableRef.current;
        if (!element || isCopying) {
            return;
        }

        setIsCopying(true);
        try {
            // Pass a Promise into ClipboardItem so the write stays tied to the user gesture
            // while the screenshot is still being rendered.
            const backgroundColor = toRgbColor(getComputedStyle(document.body).backgroundColor);
            const blobPromise = toBlob(element, {
                pixelRatio: 2,
                backgroundColor,
                cacheBust: true,
            }).then((blob) => {
                if (!blob) {
                    throw new Error("Failed to render table image");
                }
                return blob;
            });

            await navigator.clipboard.write([
                new ClipboardItem({
                    "image/png": blobPromise,
                }),
            ]);
            toast.success(t("darwin_table_image_copied"));
        } catch {
            toast.error(t("darwin_table_image_copy_failed"));
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <div className="group/table relative my-2 w-full max-w-full">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        aria-label={t("darwin_copy_table_image")}
                        disabled={isCopying}
                        onClick={handleCopyAsImage}
                        className={cn(
                            "absolute right-1 top-1 z-10 h-7 w-7 shadow-sm",
                            "opacity-0 transition-opacity group-hover/table:opacity-100 focus-visible:opacity-100",
                            isCopying && "opacity-100"
                        )}
                    >
                        <Icon
                            icon={isCopying ? TbLoader2 : TbPhoto}
                            size={14}
                            className={isCopying ? "animate-spin" : undefined}
                        />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{t("darwin_copy_table_image")}</TooltipContent>
            </Tooltip>
            <div className="overflow-x-auto">
                <div ref={tableRef} className="w-max min-w-full rounded-lg border border-border bg-background">
                    <table className="w-max min-w-full border-collapse text-sm">{children}</table>
                </div>
            </div>
        </div>
    );
}
